import { useEffect } from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'

import { Modal, useModals } from 'almanac-ui/Modal'
import { RecipientsSelectorValueType } from 'types'
import { useAuth } from 'hooks/UseAuth'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import useShareSheetV2 from 'hooks/useShareSheetV2'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'

import { ShareSheetProps } from './Types'
import { Header } from './Header'
import { InviteUsers } from './InviteUsers'
import { Accesses } from './Accesses'
import { LoadingState } from './LoadingState'
import { LoadingHeader } from './LoadingHeader'
import { TransferOwnershipConfirmation } from './TransferOwnershipConfirmation'
import { InviteCollaborators } from './InviteCollaborators'
import { InviteToWorkspace } from './InviteToWorkspace'
import { TakeOwnershipConfirmation } from './TakeOwnershipConfirmation'
import { Footer } from './Footer'

import Styles from './ShareSheet.module.scss'
import { ConfirmPriceIncreaseModal } from 'components/ConfirmPriceIncreaseModal/ConfirmPriceIncreaseModal'
import { useCurrentPlan } from 'hooks/useCurrentPlan'
import { usePaymentPlans } from 'hooks/usePaymentPlans'
import { EmailValue } from './RecipientSelector/Types'

export const ShareSheet = observer(({ file, initialPermission, initialRecipients }: ShareSheetProps) => {
  const auth = useAuth()
  const workspace = useCurrentWorkspace()
  const { openModal } = useModals()
  const { data: currentPlan } = useCurrentPlan()
  const { paymentPlans } = usePaymentPlans()

  const {
    loadShareSheet,
    loading,
    resetShareSheet,
    setShareSheet,
    asset,
    recipients,
    inviteUsers,
    inviteToWorkspace,
    isSubmitting,
    transferOwnershipUser,
    takeOwnership,
    showInviteToWorkspaceScreen,
  } = useShareSheetV2(file)

  const { isMobile } = useResponsiveDesign()

  useEffect(() => {
    loadShareSheet({
      initialPermission,
      initialRecipients,
    })
  }, [auth?.id, file, loadShareSheet, initialPermission, initialRecipients])

  useEffect(() => {
    return () => {
      resetShareSheet()
    }
  }, [file]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleInviteToWorkspace = () => {
    if (!workspace || !currentPlan) return

    const plan = paymentPlans.find((p) => p.key === currentPlan.plan)

    const isBillingMonthly = currentPlan.billing_frequency === 'month'
    const newMemberCount = recipients.filter((rec) => rec.type === RecipientsSelectorValueType.Email).length
    const isPriceIncrease = plan?.member_limit && workspace.members_count + newMemberCount > plan.member_limit

    if (inviteToWorkspace && isPriceIncrease && !workspace.early_adopter) {
      openModal(ConfirmPriceIncreaseModal, {
        currentPlan: currentPlan.plan,
        onConfirm: inviteUsers,
        newMemberCount,
        isBillingMonthly,
      })
      return
    }

    inviteUsers()
  }

  if (showInviteToWorkspaceScreen)
    return (
      <Modal.Root size={isMobile ? 'xs' : 'sm'}>
        <InviteToWorkspace
          onSubmit={handleInviteToWorkspace}
          goBack={() => setShareSheet({ showInviteToWorkspaceScreen: false })}
          isSubmitting={isSubmitting}
          inviteToWorkspace={inviteToWorkspace}
          setInviteToWorkspace={(invite: boolean) => setShareSheet({ inviteToWorkspace: invite })}
          newRecipients={recipients.filter((r) => r.type === RecipientsSelectorValueType.Email) as EmailValue[]}
        />
      </Modal.Root>
    )

  if (!!recipients.length)
    return (
      <Modal.Root size={isMobile ? 'xs' : 'sm'}>
        <InviteCollaborators file={file} />
      </Modal.Root>
    )

  if (takeOwnership && asset && auth) {
    return <TakeOwnershipConfirmation newOwnerId={auth.id} file={file} />
  }

  if (!!transferOwnershipUser && asset) {
    return <TransferOwnershipConfirmation newOwner={transferOwnershipUser} file={file} />
  }

  return (
    <Modal.Root size={isMobile ? 'xs' : 'sm'}>
      <Modal.Header title={<>{loading ? <LoadingHeader /> : <Header asset={asset} />}</>} />
      <Modal.Content>
        <div className={cx(Styles.shareSheetContents, { [Styles.layerContents]: asset?.type === 'layer' })}>
          <InviteUsers file={file} />
          {loading ? <LoadingState /> : <Accesses file={file} />}
        </div>
      </Modal.Content>
      <Footer file={file} />
    </Modal.Root>
  )
})
